import { Autocomplete, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { Breadcrumbs, Prompt, Typography } from '@weareopenr/component-library';
import { getHierarchyNames } from 'src/helpers';
import { DEBOUNCE_DURATION } from 'src/constants';
import { filterValidMoveToCategories } from './utilities';
import { IVenueCategory } from 'src/api/getVenueCategories/types';
import { IPricingCategory } from 'src/api/getPricingCategories/types';
import styles from './MoveToCategoryPrompt.module.scss';

const MAX_BREADCRUMB_LEVEL = 2;

export type TMoveToCategoryItem = IVenueCategory | IPricingCategory;

interface MoveToCategoryPromptProps<T extends TMoveToCategoryItem> {
  open: boolean;
  categoryName: string;
  orderedCategories: T[];
  category: T;
  onClose: () => void;
  onSubmit: (newParentId: string | null) => void;
  isSubmitting?: boolean;
  confirmationMessage?: string;
  subtitle?: string;
}

export const MoveToCategoryPrompt = <T extends TMoveToCategoryItem>({
  open,
  categoryName,
  orderedCategories,
  category,
  onClose,
  onSubmit: onSubmitCallback,
  isSubmitting = false,
  confirmationMessage,
  subtitle,
}: MoveToCategoryPromptProps<T>) => {
  const { handleSubmit, control, watch } = useForm<FieldValues>({
    defaultValues: { moveToCategory: null, confirmed: false },
  });
  const [delayedSearchQuery, setDelayedSearchQuery] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const selectedCategory = watch('moveToCategory');
  const isConfirmed = watch('confirmed');

  const isButtonDisabled = !selectedCategory || (confirmationMessage && !isConfirmed);

  const categories = useMemo(() => {
    const validCategories = filterValidMoveToCategories(category, orderedCategories);

    return validCategories.map((cat) => ({
      ...cat,
      breadcrumbs: getHierarchyNames(cat, orderedCategories, {
        id: 'category_id',
        parent_id: 'parent_category_id',
      }).map((breadcrumb) => ({ label: breadcrumb })),
    }));
  }, [orderedCategories, category]);

  const filteredCategories = useMemo(() => {
    if (!delayedSearchQuery) return categories;

    const searchValue = delayedSearchQuery.toLowerCase();
    return categories.filter((cat) => cat.name.toLowerCase().includes(searchValue));
  }, [categories, delayedSearchQuery]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDelayedSearchQuery(searchQuery);
    }, DEBOUNCE_DURATION);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const onSubmit = (data: FieldValues) => {
    if (confirmationMessage && !data.confirmed) return;
    onSubmitCallback(data.moveToCategory?.category_id ?? null);
  };

  return (
    <Prompt
      promptHeading={`Move "${categoryName}" to a different category?`}
      closePrompt={onClose}
      open={open}
      size="small"
      backButton
      backButtonText="Cancel"
      buttonText="Save"
      buttonType="submit"
      buttonFormId="move-to-category-form"
      isSubmitting={isSubmitting}
      isButtonDisabled={isButtonDisabled}
    >
      <form id="move-to-category-form" className={styles['move-form']} onSubmit={handleSubmit(onSubmit)}>
        {subtitle && (
          <Typography size="medium" className={styles['subtitle']}>
            {subtitle}
          </Typography>
        )}
        <Controller
          name="moveToCategory"
          control={control}
          rules={{ required: 'Please select a category' }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Autocomplete
              className={styles['autocomplete']}
              options={filteredCategories}
              getOptionLabel={(option: T) => option.name || ''}
              isOptionEqualToValue={(option: T, value: T) => option?.category_id === value?.category_id}
              onClose={() => {
                setSearchQuery('');
                setDelayedSearchQuery('');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose a Parent Category"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  error={Boolean(error)}
                  helperText={error?.message}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} className={styles['list-option']}>
                  <Breadcrumbs breadcrumbs={option.breadcrumbs} maxLevel={MAX_BREADCRUMB_LEVEL} />
                </li>
              )}
              value={value}
              onChange={(_e: SyntheticEvent<Element, Event>, category: T) => onChange(category)}
            />
          )}
        />
        {confirmationMessage && (
          <Controller
            name="confirmed"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={<Checkbox checked={value} onChange={onChange} />}
                label={
                  <Typography size="medium" weight="regular" className={styles['confirmation-checkbox']}>
                    {confirmationMessage}
                  </Typography>
                }
                className={styles['confirmation-checkbox']}
              />
            )}
          />
        )}
      </form>
    </Prompt>
  );
};
